import React, { useState } from 'react';

interface Props {
  marketId: string;
  apiBase:  string;
}

type Oracle = 'social' | 'finance';

const ORACLE_OPTIONS: { value: Oracle; label: string }[] = [
  { value: 'social',  label: 'Social Oracle (Everyday / Twitter)' },
  { value: 'finance', label: 'Finance Oracle (Wall Street / FinBERT)' },
];

export default function NewsPanel({ marketId, apiBase }: Props): React.JSX.Element {
  const [headline,       setHeadline]       = useState('');
  const [selectedOracle, setSelectedOracle] = useState<Oracle>('social');
  const [status,         setStatus]         = useState<{ msg: string; ok: boolean } | null>(null);
  const [busy,           setBusy]           = useState(false);

  async function submit(): Promise<void> {
    const text = headline.trim();
    if (!text) return;
    setBusy(true);
    setStatus(null);
    try {
      const res = await fetch(`${apiBase}/api/amm/vibe_shift`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ market_id: marketId, text, oracle: selectedOracle }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setStatus({ msg: `Oracle score: ${data.score.toFixed(2)}`, ok: true });
      setHeadline('');
    } catch (e) {
      setStatus({ msg: (e as Error).message, ok: false });
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') submit();
  }

  const inputBase: React.CSSProperties = {
    background:   '#111827',
    border:       '1px solid #1e3a5a',
    borderRadius: 4,
    color:        '#a0b8cc',
    fontSize:     12,
    fontFamily:   'monospace',
    padding:      '6px 10px',
    outline:      'none',
  };

  return (
    <div
      className="w-full max-w-3xl"
      style={{
        background:   '#0d1117',
        border:       '1px solid #1a2a3a',
        borderRadius: 6,
        padding:      '10px 14px',
      }}
    >
      <div
        style={{
          color:         '#4b6080',
          fontSize:      9,
          letterSpacing: '0.2em',
          fontFamily:    'monospace',
          marginBottom:  8,
        }}
      >
        SENTIMENT ORACLE INJECTOR
      </div>

      <div className="flex gap-2" style={{ marginBottom: 8 }}>
        {/* Oracle selector */}
        <select
          value={selectedOracle}
          onChange={e => setSelectedOracle(e.target.value as Oracle)}
          disabled={busy}
          style={{
            ...inputBase,
            flex:   '0 0 auto',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {ORACLE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2">
        {/* Headline input */}
        <input
          type="text"
          value={headline}
          onChange={e => setHeadline(e.target.value)}
          onKeyDown={onKey}
          placeholder="Enter news headline / tweet…"
          disabled={busy}
          style={{ ...inputBase, flex: 1 }}
        />

        {/* Submit button */}
        <button
          onClick={submit}
          disabled={busy || !headline.trim()}
          style={{
            background:   busy ? '#0d2030' : '#0d3a5a',
            border:       '1px solid #1e5a8a',
            borderRadius: 4,
            color:        busy ? '#2a4a6a' : '#7ab8e8',
            fontSize:     11,
            fontFamily:   'monospace',
            padding:      '6px 14px',
            cursor:       busy || !headline.trim() ? 'not-allowed' : 'pointer',
            whiteSpace:   'nowrap',
          }}
        >
          {busy ? 'ANALYSING…' : 'SUBMIT TO ORACLE'}
        </button>
      </div>

      {status && (
        <div
          style={{
            marginTop:  6,
            fontSize:   10,
            fontFamily: 'monospace',
            color:      status.ok ? '#34d399' : 'rgba(239,68,68,0.8)',
          }}
        >
          {status.msg}
        </div>
      )}
    </div>
  );
}
